import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

import { EnvironmentService } from '@ecm/consumer';
import { EcmDispatcherModule, EnvUtils, OrangeAppInfoService } from '@ecm/dispatcher';

import { OriginCode } from '../business/kenobi/origin-code.enum';
import { TrackingUtils } from '../utils/tracking.utils';

/**
 * Resolves the `codeIdor` Kenobi param : the origin application of the user journey 🚪
 *
 * Priority is evaluated on each read :
 *  1. native app info (`orange-app-info` local storage entry) : locked for the whole session once found
 *  2. `src` query param, updated on each navigation
 *  3. environment (legacy)
 *  4. default `HP`
 */
@Injectable({
    providedIn: 'root',
})
export class CodeIdorResolver {
    private readonly appInfoService = inject(OrangeAppInfoService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly router = inject(Router);

    /**
     * Class name for console logs
     * @type {string}
     */
    private readonly className: string = 'CodeIdorResolver';

    /**
     * `codeIdor` from native app info, locked for the whole session once resolved
     * @type {OriginCode}
     */
    private lockedCodeIdor: OriginCode | null = null;

    /**
     * Last `src` query param value seen on navigation
     * @type {OriginCode}
     */
    private queryParamCodeIdor: OriginCode | null = null;

    public constructor() {
        // The queryParams of activatedRoute works only for the route used for constructor
        // We have to watch router navigation event to get it from root route
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                map(() => TrackingUtils.getSrcQueryParam(this.router.routerState.root.snapshot.queryParams)),
                filter((srcValue) => srcValue !== undefined),
            )
            .subscribe({ next: (srcValue) => (this.queryParamCodeIdor = srcValue) });
    }

    /**
     * `codeIdor` for the current session, resolved on first read
     * @returns {OriginCode | null}
     */
    public get codeIdor(): OriginCode | null {
        if (this.lockedCodeIdor) {
            return this.lockedCodeIdor;
        }

        const appInfoCodeIdor = this.getCodeIdorFromAppInfo();
        if (appInfoCodeIdor) {
            this.lockedCodeIdor = appInfoCodeIdor;
            EcmDispatcherModule.sendConsoleInfo(this.className, `codeIdor from native app info: ${appInfoCodeIdor}`);
            return appInfoCodeIdor;
        }

        const queryParamCodeIdor = this.getCodeIdorFromQueryParam();
        if (queryParamCodeIdor) {
            return queryParamCodeIdor;
        }

        // eslint-disable-next-line deprecation/deprecation -- legacy fallback, kept on purpose
        return this.getCodeIdorFromEnvironment();
    }

    /**
     * Get `codeIdor` from the `name` property of the native app info
     * Value is sent as is, without any transcoding (reading and validation delegated to `OrangeAppInfoService`)
     * @returns {OriginCode} `null` when absent or invalid
     */
    private getCodeIdorFromAppInfo(): OriginCode | null {
        const appName = this.appInfoService.appName;

        return appName ? <OriginCode>appName : null;
    }

    /**
     * Get `codeIdor` from the `src` query param, either the one seen on navigation or the current one
     * @returns {OriginCode} `null` when absent
     */
    private getCodeIdorFromQueryParam(): OriginCode {
        const snapshotCodeIdor: OriginCode = TrackingUtils.getSrcQueryParam(
            this.router.routerState.root.snapshot.queryParams,
        );

        return this.queryParamCodeIdor || snapshotCodeIdor || null;
    }

    /**
     * LEGACY - Get `codeIdor` from environment (`env.ORIGIN`, or `env.BRAND` for webview app)
     * Kept as a fallback for apps still deployed with those keys
     * @returns {OriginCode} `HP` when no environment origin is defined
     * @deprecated superseded by the native app info and the `src` query param
     */
    private getCodeIdorFromEnvironment(): OriginCode {
        const env = this.environmentService.instant();

        if (env?.ORIGIN) {
            const envOrigin: string = env.ORIGIN.toUpperCase();
            return OriginCode[envOrigin] || envOrigin;
        }

        if (EnvUtils.isWebview() && env?.BRAND) {
            const envBrand: string = env.BRAND.toLowerCase();
            return envBrand === 'sosh' ? OriginCode.MYSSOSH : OriginCode.OEM;
        }

        return OriginCode.HP;
    }
}
